import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Building2,
  Landmark,
  TrendingUp,
  TrendingDown,
  FileText,
  Clock,
  Plus,
  ArrowRight,
  Wallet,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CreditorDashboardPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [assignments, setAssignments] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [recentRequests, setRecentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  // Fetch creditor assignments on mount
  useEffect(() => {
    fetchAssignments();
  }, []);

  // Fetch dashboard data when assignment changes
  useEffect(() => {
    if (selectedAssignment) {
      fetchDashboardData();
      fetchRecentRequests();
    }
  }, [selectedAssignment]);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/creditor-requests/my-assignments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssignments(response.data);
      
      // Auto-select first assignment if available
      if (response.data.length > 0) {
        setSelectedAssignment(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch assignments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    if (!selectedAssignment) return;
    
    try {
      setDashboardLoading(true);
      const response = await axios.get(
        `${API}/api/creditor-requests/dashboard/${selectedAssignment.store_id}/${selectedAssignment.creditor_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setDashboardLoading(false);
    }
  };

  const fetchRecentRequests = async () => {
    if (!selectedAssignment) return;
    
    try {
      const response = await axios.get(
        `${API}/api/creditor-requests?store_id=${selectedAssignment.store_id}&creditor_id=${selectedAssignment.creditor_id}&limit=5`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRecentRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    }
  };

  const handleAssignmentChange = (value) => {
    const assignment = assignments.find(
      a => `${a.store_id}-${a.creditor_id}` === value
    );
    setSelectedAssignment(assignment);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'reconciled':
        return <Badge className="bg-green-500/20 text-green-400"><CheckCircle2 className="h-3 w-3 mr-1" />Reconciled</Badge>;
      case 'unreconciled':
        return <Badge className="bg-amber-500/20 text-amber-400"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  if (assignments.length === 0) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Creditor Dashboard</h1>
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <AlertCircle className="h-12 w-12 mb-4 opacity-50" />
            <p className="text-lg font-medium">No Creditor Accounts Assigned</p>
            <p className="text-sm mt-2">Please contact your administrator to assign creditor accounts to your profile.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="creditor-dashboard">
      {/* Header with Account Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Creditor Dashboard</h1>
          <p className="text-muted-foreground">Manage your credit and repayment requests</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select
            value={selectedAssignment ? `${selectedAssignment.store_id}-${selectedAssignment.creditor_id}` : ''}
            onValueChange={handleAssignmentChange}
          >
            <SelectTrigger className="w-[280px]" data-testid="account-selector">
              <SelectValue placeholder="Select Account" />
            </SelectTrigger>
            <SelectContent>
              {assignments.map(a => (
                <SelectItem key={`${a.store_id}-${a.creditor_id}`} value={`${a.store_id}-${a.creditor_id}`}>
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span>{a.store_name}</span>
                    <span className="text-muted-foreground">-</span>
                    <Landmark className="h-4 w-4 text-muted-foreground" />
                    <span>{a.creditor_name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button 
            onClick={() => navigate('/creditor-requests/new')}
            data-testid="new-request-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Request
          </Button>
        </div>
      </div>

      {/* Dashboard Cards */}
      {dashboardLoading ? (
        <div className="flex items-center justify-center h-32">
          <div className="spinner" />
        </div>
      ) : dashboardData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Store & Creditor Info */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Account
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-blue-400" />
                  <span className="font-medium">{dashboardData.store_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Landmark className="h-4 w-4 text-purple-400" />
                  <span className="text-sm text-muted-foreground">{dashboardData.creditor_name}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Credit Total */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-red-400" />
                Total Credits (Owed)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-red-400">
                {formatCurrency(dashboardData.credit_total)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Money owed to you</p>
            </CardContent>
          </Card>

          {/* Payment Total */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <TrendingDown className="h-4 w-4 text-green-400" />
                Total Payments (Received)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-green-400">
                {formatCurrency(dashboardData.payment_total)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Payments received</p>
            </CardContent>
          </Card>

          {/* Current Balance */}
          <Card className="border-primary/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Wallet className="h-4 w-4 text-primary" />
                Outstanding Balance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={`text-2xl font-bold ${dashboardData.current_balance > 0 ? 'text-amber-400' : 'text-green-400'}`}>
                {formatCurrency(dashboardData.current_balance)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {dashboardData.current_balance > 0 ? 'Amount still owed' : 'Fully settled'}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Pending Requests Alert */}
      {dashboardData && dashboardData.pending_requests > 0 && (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardContent className="flex flex-col sm:flex-row sm:items-center justify-between p-4 gap-4">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-amber-400" />
              <div>
                <p className="font-medium">You have {dashboardData.pending_requests} pending request(s)</p>
                <p className="text-sm text-muted-foreground">Awaiting reconciliation by admin</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {dashboardData.pending_credit_total > 0 && (
                <div className="flex items-center gap-2 text-sm">
                  <TrendingUp className="h-4 w-4 text-red-400" />
                  <span className="text-muted-foreground">Credits:</span>
                  <span className="font-semibold text-red-400">{formatCurrency(dashboardData.pending_credit_total)}</span>
                </div>
              )}
              {dashboardData.pending_repayment_total > 0 && (
                <div className="flex items-center gap-2 text-sm">
                  <TrendingDown className="h-4 w-4 text-green-400" />
                  <span className="text-muted-foreground">Repayments:</span>
                  <span className="font-semibold text-green-400">{formatCurrency(dashboardData.pending_repayment_total)}</span>
                </div>
              )}
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/creditor-requests/history')}
              >
                View All
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions & Recent Requests */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
            <CardDescription>Submit new requests</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button 
              className="w-full justify-start" 
              variant="outline"
              onClick={() => navigate('/creditor-requests/new', { state: { type: 'credit_request', assignment: selectedAssignment } })}
              data-testid="credit-request-btn"
            >
              <TrendingUp className="h-4 w-4 mr-3 text-red-400" />
              Request Credit
              <ArrowRight className="h-4 w-4 ml-auto" />
            </Button>
            <Button 
              className="w-full justify-start" 
              variant="outline"
              onClick={() => navigate('/creditor-requests/new', { state: { type: 'repayment_request', assignment: selectedAssignment } })}
              data-testid="repayment-request-btn"
            >
              <TrendingDown className="h-4 w-4 mr-3 text-green-400" />
              Log Repayment
              <ArrowRight className="h-4 w-4 ml-auto" />
            </Button>
          </CardContent>
        </Card>

        {/* Recent Requests */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Recent Requests</CardTitle>
              <CardDescription>Your latest submissions</CardDescription>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/creditor-requests/history')}
            >
              View All
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {recentRequests.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No requests yet</p>
                <p className="text-sm">Submit your first request to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentRequests.map(request => (
                  <div 
                    key={request.id} 
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {request.request_type === 'credit_request' ? (
                        <TrendingUp className="h-5 w-5 text-red-400" />
                      ) : (
                        <TrendingDown className="h-5 w-5 text-green-400" />
                      )}
                      <div>
                        <p className="font-medium">
                          {request.request_type === 'credit_request' ? 'Credit Request' : 'Repayment'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(request.date)} • {request.reference || 'No reference'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-semibold ${request.request_type === 'credit_request' ? 'text-red-400' : 'text-green-400'}`}>
                        {formatCurrency(request.amount)}
                      </p>
                      {getStatusBadge(request.reconciliation_status)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
